const _ = require("lodash");
const Math2D = require("../../core/util/Math2D");

// You should not directly use this file. It's the minimal representation of
// a geometry that GridGeometry turns into a full interface. Use GridGeometry.
// See SquareGridGeometryCore for any special notes about the interfaces for
// these functions.

// Hexagonal grid sizes are specified in rows and columns. There are
// cols*rows hexagonal cells, with the rows alternating between slightly to
// left and slightly to right (top row is left). In particular the cells are
// oriented so two sides are perfectly vertical and no sides are horizontal.
// - Cells are identified by [i, j] with 0 <= i < cols and 0 <= j < rows.
// - Vertices are identified by [i, j] with 0 <= i <= 2*cols+1 and 0 <= j <=
//   rows. Note that two of these vertices don't exist: [2*cols+1, 0], and either
//   [2*cols, rows] (if rows is odd) or [0, rows] (if rows is even).
// - Edges are identified by [i, j, xdir] with [i, j] a cell (possibly outside
//   the grid) and xdir one of -1, 0, 1. The edge emanates from the upper-left
//   vertex of the cell. Three edges come out of that vertex: xdir -1 means
//   up and left, xdir 1 means up and right, and xdir 0 means straight down.

// Cell bounding boxes have total width rt(3)/2 and total height 1 in the non-
// dim coordinate system. This means hexagons have side length 1/2.

// Rough ASCII diagram of cell/vertex coordinates in a 2 by 2 grid.
//
//         Cells & Vertices
//
//      _1,0_       _3,0_
// 0,0_-     -_2,0_-     -_4,0
//  |           |           |
//  |    0,0    |    1,0    |
//  |           |           |
// 0,1_       _2,1_       _4,1_
//     -_1,1_-     -_3,1_-     -_5,1
//        |           |           |
//        |    0,1    |    1,1    |
//        |           |           |
//       1,2_       _3,2_       _5,2
//           -_2,2_-     -_4,2_-
//
//           Edges
//
//           -^-               -^-
//     0,0,+1   1,0,-1   1,0,+1   2,0,-1
//   |                 |                 |
// 0,0,0             1,0,0             2,0,0
//   |                 |                 |
//     0,1,-1   0,1,+1   1,1,-1   1,1,+1   2,1,-1
//            X                 X                 >
//            |                 |                 |
//          0,1,0             1,1,0             2,1,0
//            |                 |                 |
//              1,2,-1   1,2,+1   2,2,-1   2,2,+1
//                     V                 V

// Constant used in a lot of computations.
const HRT3 = Math.sqrt(3) / 2;

/**
 * Was getting "Declaration emit for this file requires using private name".
 * This `type` annotation is a workaround. See: https://stackoverflow.com/questions/60009092/declaration-will-not-emit-due-to-private-name-usage
 * @type {unknown}
 */
const HexagonGridRowsGeometryCore = function(cols, rows, options) {
	this.style = "hexRows";
	this.cols = cols;
	this.rows = rows;
	this.options = options || {};
	this.noLeft = !!this.options.noLeft;
	this.noRight = !!this.options.noRight;
};

// Internal helper. Get range of cell x coordinates in [min, max + 1] form.
const getRowCellRange = function(core, y) {
	return [
		core.noLeft && y % 2 === 0 ? 1 : 0,
		core.cols - (core.noRight && y % 2 !== 0 ? 1 : 0),
	];
};

HexagonGridRowsGeometryCore.prototype.getBaseWidth = function() {
	const evenRange = getRowCellRange(this, 0);
	const oddRange = getRowCellRange(this, 1);
	const minX = Math.min(evenRange[0], oddRange[0] + 0.5);
	const maxX = Math.max(evenRange[1], oddRange[1] + 0.5);
	return (maxX - minX) * HRT3;
};

HexagonGridRowsGeometryCore.prototype.getBaseHeight = function() {
	return 0.75 * this.rows + 0.25;
};

// This should return the cells in left-to-right then top-to-bottom order.
HexagonGridRowsGeometryCore.prototype.getAllCells = function() {
	const result = [];
	for (let rowIndex = 0; rowIndex < this.rows; rowIndex++) {
		const rowRange = getRowCellRange(this, rowIndex);
		for (let colIndex = rowRange[0]; colIndex < rowRange[1]; colIndex++) {
			result.push([colIndex, rowIndex]);
		}
	}
	return result;
};

HexagonGridRowsGeometryCore.prototype.convertEdgeZ = function(xDir) {
	return parseInt(xDir);
};

// Order matters: the edges must go clockwise around the cell
HexagonGridRowsGeometryCore.prototype.getCellEdges = function(x, y) {
	const yParity = Math.abs(y % 2);

	return [
		[x + 1, y, -1],
		[x + 1, y, 0],
		[x + yParity, y + 1, 1],
		[x + yParity, y + 1, -1],
		[x, y, 0],
		[x, y, 1],
	];
};

HexagonGridRowsGeometryCore.prototype.getEdgeCells = function(x, y, xDir) {
	const yParity = Math.abs(y % 2);
	if (xDir === -1) {
		return [
			[x - 1, y],
			[x - 1 + yParity, y - 1],
		];
	} else if (xDir === 0) {
		return [
			[x - 1, y],
			[x, y],
		];
	} else if (xDir === 1) {
		return [
			[x - 1 + yParity, y - 1],
			[x, y],
		];
	} else {
		throw new Error("Invalid edge xDir " + xDir);
	}
};

HexagonGridRowsGeometryCore.prototype.getEdgeVertices = function(x, y, xDir) {
	const yParity = Math.abs(y % 2);
	// [vertexX, y] is upper-left vertex of the cell [x, y].
	const vertexX = 2 * x + yParity;
	if (xDir === -1) {
		return [
			[vertexX - 1, y],
			[vertexX, y],
		];
	} else if (xDir === 0) {
		return [
			[vertexX, y],
			[vertexX, y + 1],
		];
	} else if (xDir === 1 || xDir === true) {
		return [
			[vertexX, y],
			[vertexX + 1, y],
		];
	} else {
		throw new Error("Invalid edge xDir " + xDir);
	}
};

// Order matters: the edges must go clockwise around the vertex.
HexagonGridRowsGeometryCore.prototype.getVertexEdges = function(x, y) {
	const yParity = Math.abs(y % 2);
	// [cellX, y] is either cell below and to right of vertex or cell right below.
	const cellX = Math.floor((x - yParity) / 2);
	if ((x + y) % 2) {
		// Cell is right below.
		return [
			[cellX + yParity, y - 1, 0],
			[cellX + 1, y, -1],
			[cellX, y, 1],
		];
	} else {
		// Cell is below and to right, so all edges are based off it.
		return [
			[cellX, y, 1],
			[cellX, y, 0],
			[cellX, y, -1],
		];
	}
};

HexagonGridRowsGeometryCore.prototype.getCollinearEdge = function(
	edge,
	vertex
) {
	return null;
};

HexagonGridRowsGeometryCore.prototype.getAllNonOverlapEdges = function(geom) {
	let cellEdges = "";
	//get each cell
	const allCells = this.getAllCells();
	_.each(allCells, cell => {
		//get edges of each cell
		const edgesOfCell = this.getCellEdges(cell[0], cell[1], cell[2]);
		//check if edge already exists
		_.each(edgesOfCell, edgeCell => {
			const serializeEdge =
				edgeCell[0] + "," + edgeCell[1] + "," + edgeCell[2] + "#";
			//if already found, remove previously found
			if (_.includes(cellEdges, serializeEdge)) {
				cellEdges = _.replace(cellEdges, serializeEdge, "");
			}
			//otherwise, add to cell edges
			else {
				cellEdges += serializeEdge;
			}
		});
	});
	const deserialize = _.split(cellEdges, "#");
	//remove last empty item given by delimeter
	deserialize.pop();

	//each string, to array of numbers
	const convertedCellEdges = _.map(deserialize, edge => {
		return _.map(_.split(edge, ","), Number);
	});

	return convertedCellEdges;
};

HexagonGridRowsGeometryCore.prototype.getIntersectingEdgeBetweenTwoCells = function(
	geom,
	cell1,
	cell2
) {
	//cell should not be the same
	if (_.isEqual(cell1, cell2)) return null;
	const edgesOfCell1 = geom.getCellEdges(cell1[0], cell1[1]);
	const edgesOfCell2 = geom.getCellEdges(cell2[0], cell2[1]);

	let found = null;
	//look for matching edge
	_.each(edgesOfCell1, edge1 => {
		_.each(edgesOfCell2, edge2 => {
			if (_.isEqual(edge1, edge2)) {
				found = edge1;
			}
		});
	});
	//return a found edge, or null if not found
	return found;
};

HexagonGridRowsGeometryCore.prototype.getDrawCellRect = function(dim, x, y) {
	const center = this.getDrawCellCenter(dim, x, y);
	return {
		x: center[0] - 0.375 * dim.size,
		y: center[1] - 0.375 * dim.size,
		w: 0.75 * dim.size,
		h: 0.75 * dim.size,
	};
};

HexagonGridRowsGeometryCore.prototype.getDrawCellCenter = function(dim, x, y) {
	const xNudge =
		(HRT3 / 2) * (1 + (this.noLeft ? -1 : 0) + (y % 2 === 0 ? 0 : 1));
	return [
		dim.x + (xNudge + x * HRT3) * dim.size,
		dim.y + (0.5 + y * 0.75) * dim.size,
	];
};

HexagonGridRowsGeometryCore.prototype.getDrawVertex = function(dim, x, y) {
	const xNudge = (HRT3 / 2) * (this.noLeft ? -1 : 0);
	const yNudge = (x + y) % 2 === 0 ? 0.25 : 0;
	return [
		dim.x + (xNudge + (x * HRT3) / 2) * dim.size,
		dim.y + (yNudge + y * 0.75) * dim.size,
	];
};

HexagonGridRowsGeometryCore.prototype.mouseToGrid = function(
	dim,
	mouseX,
	mouseY
) {
	return [
		(mouseX - dim.x) / (HRT3 * dim.size) + (this.noLeft ? 0.5 : 0),
		(mouseY - dim.y) / (0.75 * dim.size),
	];
};

HexagonGridRowsGeometryCore.prototype.gridToMouse = function(
	dim,
	coordX,
	coordY
) {
	coordX += this.noLeft ? -0.5 : 0;
	return [coordX * dim.size * HRT3 + dim.x, coordY * dim.size * 0.75 + dim.y];
};

HexagonGridRowsGeometryCore.prototype.getMousedCell = function(
	coordX,
	coordY,
	clamp
) {
	const shiftedRight = Math.floor(coordY) % 2;
	const realCoordX = coordX + (shiftedRight ? -0.5 : 0);
	const fx = realCoordX - Math.floor(realCoordX);
	const fy = coordY - Math.floor(coordY);

	// Preliminary cell coordinates.
	const cell = [Math.floor(realCoordX), Math.floor(coordY)];
	// Check if we're over slanted cell boundaries and make corrections.
	if (2 * fx + 3 * fy < 1) {
		// Top-left slant.
		if (!shiftedRight) {
			cell[0] -= 1;
		}
		cell[1] -= 1;
	} else if (2 * (1 - fx) + 3 * fy < 1) {
		// Top-right slant.
		if (shiftedRight) {
			cell[0] += 1;
		}
		cell[1] -= 1;
	}

	// If clamping, make sure we get valid coordinates.
	if (clamp) {
		cell[1] = Math.max(0, Math.min(this.rows - 1, cell[1]));
		const range = getRowCellRange(this, cell[1]);
		cell[0] = Math.max(range[0], Math.min(range[1], cell[0]));
	}
	return cell;
};

HexagonGridRowsGeometryCore.prototype.isDeadCenter = function(
	deadDistance,
	coordX,
	coordY,
	centerX,
	centerY
) {
	const dist = Math2D.pointPointDistance(coordX, coordY, centerX, centerY);
	return dist < deadDistance / 2;
};

HexagonGridRowsGeometryCore.prototype.isDeadVertex = function(
	deadDistance,
	coordX,
	coordY,
	vertexX,
	vertexY
) {
	const dist = Math2D.pointPointDistance(coordX, coordY, vertexX, vertexY);
	return dist < deadDistance / 2;
};

HexagonGridRowsGeometryCore.prototype.isLooseVertexFit = function(
	coordX,
	coordY,
	vertexX,
	vertexY
) {
	const dx = vertexX - coordX;
	const dy = vertexY - coordY;
	const squareDist = 0.75 * dx * dx + 0.5625 * dy * dy;
	return squareDist > 0.05;
};

HexagonGridRowsGeometryCore.prototype.isNewEdge = function(
	coordX,
	coordY,
	newEdge,
	lastEdge
) {
	// Later if we want to do some sensitive checking of new edges, we can add this.
	return true;
};

module.exports = HexagonGridRowsGeometryCore;
